import { useMemo, useState } from "react";
import FormModal from "@components/common/HPFormModal";
import HPInput from "@components/common/Input";
import { Box, Typography } from "@mui/material";
import HPDropdown from "../common/Dropdown";
import { Client } from "@/models/Client";

interface EditDashboardModalProps {
  action: (
    dashboardName: string,
    additionalClientIds: string[],
  ) => Promise<boolean>;
  dashboardData: any;
  clients: Client[];
  closeAction: () => void;
  cancelAction: () => void;
}

const EditDashboardModal = ({
  dashboardData,
  clients,
  action,
  closeAction,
}: EditDashboardModalProps) => {
  const [dashboardName, setDashboardName] = useState(dashboardData.name);
  const [additionalClients, setAdditionalClients] = useState<string[]>(
    dashboardData.additionalClients.map((client: Client) => client.id),
  );
  const [isTouched, setIsTouched] = useState(false);
  const characterLimit = 100;
  const allowedCharactersRegex = /^[a-zA-Z0-9 _\-]*$/;

  const isDashboardNameValid =
    dashboardName.length >= 3 &&
    dashboardName.length <= characterLimit &&
    allowedCharactersRegex.test(dashboardName);

  const isFormValid = isDashboardNameValid;

  const handleSave = async () => {
    if (!isTouched) {
      setIsTouched(true);
    }
    if (isFormValid) {
      await action(dashboardName, additionalClients);
    }
  };

  const clientOptions = useMemo(() => {
    return clients.map((client) => ({
      value: client.id,
      label: client.name,
    }));
  }, [clients]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isTouched) {
      setIsTouched(true);
    }
    const newValue = e.target.value.slice(0, characterLimit);
    setDashboardName(newValue);
  };

  const getDashboardNameErrorMessage = () => {
    if (!isTouched) {
      return null;
    }
    if (dashboardName.length < 3) {
      return "At least 3 characters needed";
    }
    if (!allowedCharactersRegex.test(dashboardName)) {
      return "Dashboard name contains invalid characters";
    }
    return null;
  };

  return (
    <FormModal
      title="Edit dashboard"
      confirmButtonText="Save"
      onConfirm={handleSave}
      onCancel={closeAction}
    >
      <div className="flex flex-col gap-2">
        <HPInput
          label="Dashboard name*"
          value={dashboardName}
          onChange={handleNameChange}
          placeholder="Enter dashboard name"
          error={isTouched && !isDashboardNameValid}
        />
        <Box className="flex justify-between items-start">
          {getDashboardNameErrorMessage() && isTouched ? (
            <Typography
              variant="caption"
              sx={{ color: "var(--color-hp-error)" }}
            >
              {getDashboardNameErrorMessage()}
            </Typography>
          ) : (
            <Typography variant="caption" className="text-xs max-w-[380px] ">
              At least 3 characters needed. Letters, numbers, spaces, hyphens,
              underscores
            </Typography>
          )}

          <Typography
            variant="body2"
            className={`text-sm ${getDashboardNameErrorMessage() && "text-hp-error"}`}
          >
            {dashboardName.length}/{characterLimit}
          </Typography>
        </Box>
      </div>

      <div className="flex flex-col gap-2">
        <HPDropdown
          label="Additional clients"
          options={clientOptions.filter(
            (c) => c.value !== dashboardData.primaryClient.id,
          )}
          value={additionalClients}
          onChange={(value) => setAdditionalClients(value as string[])}
          placeholder="Select additional clients"
          multiSelect={true}
        />
      </div>
    </FormModal>
  );
};

export default EditDashboardModal;
